import { z } from "zod";
import { optionalPhoneBRSchema } from "../common/phone";
import { optionalCepSchema } from "../common/cep";
import { brazilianStateSchema } from "../user/cro";

export const partnerTypeSchema = z.enum([
  "DENTIST",
  "SUPPLIER",
  "LAB",
  "RADIOLOGY",
  "CLINIC_NETWORK",
]);
export type PartnerType = z.infer<typeof partnerTypeSchema>;

export const partnerStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);
export type PartnerStatus = z.infer<typeof partnerStatusSchema>;

export const partnerInviteStatusSchema = z.enum([
  "PENDING",
  "ACCEPTED",
  "REVOKED",
  "EXPIRED",
]);
export type PartnerInviteStatus = z.infer<typeof partnerInviteStatusSchema>;

/** CPF ou CNPJ — aceita só dígitos ou formatado. Validação leve (tamanho). */
const partnerDocumentSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\D/g, ""))
  .refine((v) => v === "" || v.length === 11 || v.length === 14, {
    message: "CPF (11) ou CNPJ (14) inválido",
  })
  .optional()
  .or(z.literal("").transform(() => undefined));

const optionalEmpty = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .or(z.literal("").transform(() => undefined));

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .optional()
  .or(z.literal("").transform(() => undefined));

const partnerBaseSchema = z.object({
  type: partnerTypeSchema,
  name: z.string().trim().min(2, "Nome muito curto").max(160),
  tradeName: optionalEmpty(160),
  email: optionalEmail,
  // Nulo apaga: é campo só do laboratório, e o "" do formulário precisa conseguir limpar.
  emailDeCobranca: z.string().trim().toLowerCase().email("E-mail inválido").nullable().optional().or(z.literal("").transform(() => null)),
  phone: optionalPhoneBRSchema,
  document: partnerDocumentSchema,
  croNumber: optionalEmpty(20),
  croState: brazilianStateSchema.optional(),
  specialty: optionalEmpty(100),
  zipCode: optionalCepSchema,
  address: optionalEmpty(200),
  addressNumber: optionalEmpty(10),
  addressComplement: optionalEmpty(100),
  neighborhood: optionalEmpty(100),
  city: optionalEmpty(100),
  state: brazilianStateSchema.optional(),
  notes: optionalEmpty(2000),
  status: partnerStatusSchema.default("ACTIVE"),
});

export const partnerCreateSchema = partnerBaseSchema;
export const partnerUpdateSchema = partnerBaseSchema.partial().extend({
  id: z.string().min(1),
  // Sem o default: no zod 4 o default vale mesmo com o campo ausente, e editar sem mandar o campo o reescreveria.
  status: partnerStatusSchema.optional(),
});

export type PartnerCreateInput = z.infer<typeof partnerCreateSchema>;
export type PartnerUpdateInput = z.infer<typeof partnerUpdateSchema>;

export const partnerListFiltersSchema = z.object({
  search: z.string().trim().max(200).optional(),
  type: partnerTypeSchema.optional(),
  status: partnerStatusSchema.optional(),
  take: z.number().int().min(1).max(500).default(100),
  skip: z.number().int().min(0).default(0),
});
export type PartnerListFilters = z.infer<typeof partnerListFiltersSchema>;

// ─── Convites ────────────────────────────────────────

export const partnerInviteCreateSchema = z.object({
  type: partnerTypeSchema,
  email: optionalEmail,
  phone: optionalPhoneBRSchema,
  /** Quantos dias antes de expirar. Default 14. */
  expiresInDays: z.number().int().min(1).max(90).default(14),
});
export type PartnerInviteCreateInput = z.infer<typeof partnerInviteCreateSchema>;

/** Schema público de aceitação — não inclui `type` (vem do convite) nem `status`. */
export const partnerInviteAcceptSchema = z.object({
  token: z.string().min(1),
  name: z.string().trim().min(2).max(160),
  tradeName: optionalEmpty(160),
  email: optionalEmail,
  phone: optionalPhoneBRSchema,
  document: partnerDocumentSchema,
  croNumber: optionalEmpty(20),
  croState: brazilianStateSchema.optional(),
  specialty: optionalEmpty(100),
  zipCode: optionalCepSchema,
  address: optionalEmpty(200),
  addressNumber: optionalEmpty(10),
  addressComplement: optionalEmpty(100),
  neighborhood: optionalEmpty(100),
  city: optionalEmpty(100),
  state: brazilianStateSchema.optional(),
});
export type PartnerInviteAcceptInput = z.infer<typeof partnerInviteAcceptSchema>;

// ─── Convite de conexão real laboratório ↔ dentista ───

/** Ou aponta pra um Partner (DENTIST) solto já cadastrado (ponte legada), ou é um convite novo —
 *  com email (chega por email e dentro do app), com nome livre pra exibição, com os dois, ou até
 *  sem nenhum (link puro). O registro do parceiro só é criado no aceite. */
export const partnerConnectInviteCreateSchema = z
  .object({
    partnerId: z.string().min(1).optional(),
    dentistDisplayName: z.string().trim().min(2, "Nome muito curto").max(160).optional(),
    email: z.string().trim().toLowerCase().email("Email inválido").max(254).optional(),
    expiresInDays: z.number().int().min(1).max(90).default(14),
    /** O laboratório decide na geração: true = o dentista convidado pode conectar VÁRIAS clínicas
     *  dele de uma vez; false = conecta uma só, escolhendo qual (padrão). */
    multiClinic: z.boolean().default(false),
  })
  // Email combina com os dois caminhos: convite novo (cria a ficha no aceite) e ponte de uma
  // ficha existente (o aceite atualiza a ficha — e o convite chega por email/app do mesmo jeito).
  // Só nome livre + ficha existente não fazem sentido juntos: a ficha já tem nome.
  .refine((data) => !data.partnerId || !data.dentistDisplayName, {
    message: "Convite de um cadastro existente não combina com nome livre.",
  });
export type PartnerConnectInviteCreateInput = z.infer<typeof partnerConnectInviteCreateSchema>;

/** Sem organizationIds = comportamento antigo, aceite na organização ativa (página pública).
 *  Com organizationIds = aceite multi-clínica: quem tem várias escolhe em quais conectar. */
export const partnerConnectInviteAcceptSchema = z.object({
  token: z.string().min(1),
  organizationIds: z.array(z.string().min(1)).min(1).max(20).optional(),
});

/** Recusa de um convite endereçado a mim (só o id). */
export const partnerConnectInviteMineSchema = z.object({
  id: z.string().min(1),
});

/** Aceite de dentro do app: o convite endereçado + as clínicas escolhidas — independente da
 *  organização ativa (a permissão é checada por clínica-alvo, não na ativa). */
export const partnerConnectInviteAcceptMineSchema = z.object({
  id: z.string().min(1),
  organizationIds: z.array(z.string().min(1)).min(1).max(20),
});

/** Lista as clínicas do usuário elegíveis pro aceite de um convite — por id (endereçado a mim) OU
 *  por token (link em mãos), nunca os dois. */
export const partnerConnectInviteClinicasSchema = z
  .object({
    id: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
  })
  .refine((d) => !!d.id !== !!d.token, { message: "Informe id OU token." });
export type PartnerConnectInviteAcceptInput = z.infer<typeof partnerConnectInviteAcceptSchema>;

// ─── Parceria (precificação personalizada) ────────────

/** Marca/desmarca um parceiro conectado como "Clínica Parceira" — libera a aba de precificação
 *  personalizada pra ele. Mutation separada de `partnerUpdateSchema`, só OWNER (ver labPricing). */
export const partnerSetIsPartnerSchema = z.object({
  id: z.string().min(1),
  isPartner: z.boolean(),
});
export type PartnerSetIsPartnerInput = z.infer<typeof partnerSetIsPartnerSchema>;

/** Vincula/desvincula uma clínica parceira a uma PriceTable nomeada — `priceTableId: null`
 *  desvincula, voltando pra config individual (ClientPricingProfile/ClientPriceOverride). */
export const partnerSetPriceTableSchema = z.object({
  id: z.string().min(1),
  priceTableId: z.string().min(1).nullable(),
});
export type PartnerSetPriceTableInput = z.infer<typeof partnerSetPriceTableSchema>;
